import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'

interface UserProfile {
  id: string
  district_id: string
  district_name?: string
  full_name?: string
  role?: string
}

interface ImpersonatedUser {
  id: string
  email: string
  full_name: string | null
}

interface AuthContextType {
  user: User | null
  session: Session | null
  userProfile: UserProfile | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signUp: (email: string, password: string, userData: any) => Promise<{ error: any }>
  signOut: () => Promise<void>
  signInWithGoogle: () => Promise<{ error: any }>
  signInWithMicrosoft: () => Promise<{ error: any }>
  isTucsonUnified: boolean
  effectiveUserId: string | null
  isImpersonating: boolean
  impersonatedUser: ImpersonatedUser | null
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(() => {
    const stored = localStorage.getItem('impersonated_user');
    return stored ? JSON.parse(stored) : null;
  })

  useEffect(() => {
    // Listen for impersonation changes
    const handleStorageChange = () => {
      const stored = localStorage.getItem('impersonated_user');
      setImpersonatedUser(stored ? JSON.parse(stored) : null);
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    console.log('[useAuth] Initial mount, fetching session...');
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log('[useAuth] Session fetched:', !!session, session?.user?.email);
      setSession(session)
      setUser(session?.user ?? null)
      
      // Set loading false immediately after getting session
      setLoading(false)
      console.log('[useAuth] Loading set to false');
      
      if (session?.user) {
        try {
          // Fetch actual user profile
          const userId = impersonatedUser?.id || session.user.id;
          console.log('[useAuth] Fetching profile for user:', userId);
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id, district_id, first_name, last_name')
            .eq('id', userId)
            .maybeSingle();
          
          console.log('[useAuth] Profile fetch result:', { profile: !!profile, error: profileError });
          
          if (profile && !profileError) {
            setUserProfile({
              id: profile.id,
              district_id: profile.district_id,
              full_name: `${profile.first_name} ${profile.last_name}`,
            });
          } else {
            console.log('[useAuth] No profile found or error:', profileError);
            setUserProfile(null);
          }
        } catch (err) {
          console.error('[useAuth] Error fetching profile:', err);
          setUserProfile(null);
        }
      }
    }).catch((error) => {
      console.error('[useAuth] Error getting session:', error)
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
      
      // Defer profile fetch to avoid blocking the callback
      if (session?.user) {
        setTimeout(() => {
          const userId = impersonatedUser?.id || session.user.id;
          supabase
            .from('profiles')
            .select('id, district_id, first_name, last_name')
            .eq('id', userId)
            .maybeSingle()
            .then(({ data: profile, error: profileError }) => {
              if (profile && !profileError) {
                setUserProfile({
                  id: profile.id,
                  district_id: profile.district_id,
                  full_name: `${profile.first_name} ${profile.last_name}`,
                });
              } else {
                setUserProfile(null);
              }
            });
        }, 0);
      } else {
        setUserProfile(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [impersonatedUser])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error }
  }

  const signUp = async (email: string, password: string, userData: any) => {
    const redirectUrl = `${window.location.origin}/`
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: userData
      }
    })
    return { error }
  }

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    })
    return { error }
  }

  const signInWithMicrosoft = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    })
    return { error }
  }

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      // Redirect to landing page after sign out
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isTucsonUnified = userProfile?.district_name?.toLowerCase().includes('tucson unified') ?? false
  const effectiveUserId = impersonatedUser?.id || user?.id || null
  const isImpersonating = !!impersonatedUser

  const value = {
    user,
    session,
    userProfile,
    loading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithMicrosoft,
    isTucsonUnified,
    effectiveUserId,
    isImpersonating,
    impersonatedUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}